// frontend/src/pages/SRPCourier.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/UIComponents";

// Single shared empty form template
const EMPTY_FORM = {
  width_from: "",
  width_to: "",
  fee: "",
  _id: null, // hd_exw_id for updates
};

/**
 * SRPCourier
 * ---------------------------------------------------------------------------
 * Purpose: Manage width-based SRP courier fees stored in public.hd_exw
 * - List filtered by selected courier (prod_line_id = courier opt_id)
 * - Insert uses selected courier_id
 * - Update by hd_exw_id (unique)
 */
export default function SRPCourier() {
  const nav = useNavigate();

  // ---------- Left: list ----------
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState("");

  // ---------- Right: form ----------
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [initialForm, setInitialForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [errorSave, setErrorSave] = useState("");
  const [hint, setHint] = useState("");

  // ---------- New: Courier Dropdown ----------
  const [courierList, setCourierList] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState(""); // opt_id

  // ---------- Responsive ----------
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 920
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 920);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const qRef = useRef(null);

  // initial load: options then list (empty until courier chosen)
  useEffect(() => {
    loadCouriers();
    setTimeout(() => qRef.current && qRef.current.focus(), 0);
  }, []);

  // reload list when a courier is selected
  useEffect(() => {
    loadList(selectedCourier);
    // clear selection form on courier change
    setForm({ ...EMPTY_FORM });
    setInitialForm({ ...EMPTY_FORM });
  }, [selectedCourier]);

  async function loadCouriers() {
    try {
      const u = new URL("/api/data", window.location.origin);
      u.searchParams.set("scope", "srp_courier");
      u.searchParams.set("action", "courier_options");
      const r = await fetch(u.toString(), { method: "GET" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      setCourierList(j.rows || []);
    } catch (e) {
      console.error("Failed to load courier options:", e);
    }
  }

  async function loadList(courierId = "") {
    try {
      setLoadingList(true);
      setErrorList("");
      const u = new URL("/api/data", window.location.origin);
      u.searchParams.set("scope", "srp_courier");
      u.searchParams.set("action", "list");
      if (courierId) u.searchParams.set("courier_id", courierId);

      const r = await fetch(u.toString(), { method: "GET" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      const arr = Array.isArray(j.rows) ? j.rows : [];
      setRows(
        arr.map((x) => ({
          cut_w: num(x.cut_w),
          cut_h: num(x.cut_h),
          exw: num(x.exw),
          hd_exw_id: Number(x.hd_exw_id) || null,
        }))
      );
    } catch (e) {
      setErrorList(String(e.message || e));
    } finally {
      setLoadingList(false);
    }
  }

  function selectRow(r) {
    const selected = {
      width_from: num(r.cut_w),
      width_to: num(r.cut_h),
      fee: num(r.exw),
      _id: r.hd_exw_id || null,
    };
    setForm(selected);
    setInitialForm(selected);
    setHint("Loaded");
  }

  function newRow() {
    setForm({ ...EMPTY_FORM });
    setInitialForm({ ...EMPTY_FORM });
    setHint("New entry");
  }

  function resetForm() {
    setForm(initialForm);
    setHint("Reset");
  }

  const changed = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  function onChange(e) {
    const { name, value } = e.target;
    if (["width_from", "width_to", "fee"].includes(name)) {
      const v = value === "" ? "" : Number(value);
      setForm((prev) => ({ ...prev, [name]: v }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function onSave() {
    setErrorSave("");
    setHint("");

    const w1 = num(form.width_from);
    const w2 = num(form.width_to);
    const fee = num(form.fee);
    if (!Number.isFinite(w1) || !Number.isFinite(w2) || !Number.isFinite(fee)) {
      setErrorSave("All fields must be numbers");
      return;
    }
    if (w1 <= 0 || w2 <= 0) {
      setErrorSave("Widths must be > 0 (mm)");
      return;
    }
    if (w2 < w1) {
      setErrorSave("Width to must be ≥ Width from");
      return;
    }

    const isUpdate = Number.isInteger(form._id);

    if (!isUpdate && !selectedCourier) {
      setErrorSave("Please select a Courier before saving a new row");
      return;
    }

    const payload = isUpdate
      ? {
          hd_exw_id: form._id,
          width_from: w1,
          width_to: w2,
          fee,
        }
      : {
          courier_id: Number(selectedCourier),
          width_from: w1,
          width_to: w2,
          fee,
        };

    setSaving(true);
    try {
      const method = isUpdate ? "PUT" : "POST";
      const r = await fetch("/api/data?scope=srp_courier", {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);

      const updated = {
        cut_w: num(j.row?.cut_w ?? w1),
        cut_h: num(j.row?.cut_h ?? w2),
        exw: num(j.row?.exw ?? fee),
        hd_exw_id: Number(j.row?.hd_exw_id ?? form._id) || null,
      };

      setRows((prev) => {
        const idx = prev.findIndex((p) => p.hd_exw_id === updated.hd_exw_id);
        if (idx >= 0) {
          const next = prev.slice();
          next[idx] = updated;
          return next.sort((a, b) => a.cut_w - b.cut_w);
        }
        return [updated, ...prev].sort((a, b) => a.cut_w - b.cut_w);
      });

      selectRow(updated);
      setHint(isUpdate ? "Saved changes" : "Inserted");
    } catch (e) {
      setErrorSave(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  function back() {
    nav("/purchase/config");
  }

  // ---------- Simple layout consistent with your app ----------
  const grid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "360px 1fr",
    gap: isNarrow ? 10 : 16,
    alignItems: "start",
  };

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "210px 1fr",
    gap: 10,
    alignItems: "center",
  };

  const smallText = { fontSize: isNarrow ? 11 : 12, opacity: 0.75 };
  const listItemText = isNarrow ? 15 : 14;

  return (
    <div className="app-page">
      <main className="container" style={isNarrow ? { fontSize: 15 } : undefined}>
        <h1 className="menu-title" style={isNarrow ? { fontSize: 22, lineHeight: "24px" } : undefined}>
          Tuiss Web Tools — Purchase
        </h1>
        <h2 style={{ marginTop: -8, ...(isNarrow ? { fontSize: 16 } : {}) }}>
          SRP Courier — Width-based Fees
        </h2>

        {/* Top buttons */}
        <div style={{ margin: "8px 0 12px", display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button onClick={back}>← Trở về / Back</Button>
          <Button onClick={newRow}>New</Button>
          <Button onClick={resetForm} disabled={!changed}>Reset</Button>
          <Button onClick={onSave} disabled={saving || (!changed && form._id != null)}>
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button 
            onClick={() => nav("/purchase/config/shipping-crates")}
            style={{ marginLeft: "auto", border: "1px solid #ff4a4a", color: "#d32f2f" }}
          >
            Bulk Shipment Surcharge
          </Button>
          {hint ? <span style={{ marginLeft: 4, ...smallText }}>{hint}</span> : null}
        </div>

        {/* Courier Dropdown */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, marginRight: 8 }}>Courier:</label>
          <select
            value={selectedCourier}
            onChange={(e) => setSelectedCourier(e.target.value)}
            style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid #ccc", minWidth: 240 }}
          >
            <option value="">-- Select Courier --</option>
            {courierList.map((c) => (
              <option key={c.opt_id} value={c.opt_id}>
                {c.opt_name}
              </option>
            ))}
          </select>
        </div>

        <div style={grid}>
          {/* LEFT: list */}
          <section className="box" style={{ overflow: "hidden" }}>
            <label style={{ display: "block", fontWeight: 600, ...(isNarrow ? { fontSize: 14 } : {}) }}>
              Records (Width from → Width to → Fee)
            </label>

            <div style={{ marginTop: 6, ...smallText }}>
              {loadingList ? "Loading..." : rows.length + " item(s)"}
              {errorList ? <span style={{ color: "crimson", marginLeft: 8 }}>{errorList}</span> : null}
              <Button onClick={() => loadList(selectedCourier)} style={{ marginLeft: 8 }}>Refresh</Button>
            </div>

            <div
              style={{
                marginTop: 8,
                maxHeight: isNarrow ? 320 : 440,
                overflow: "auto",
                borderTop: "1px solid #eee",
              }}
            >
              {rows.map((r) => {
                const sel = form._id === r.hd_exw_id;
                return (
                  <button
                    key={r.hd_exw_id ?? `${r.cut_w}-${r.cut_h}`}
                    onClick={() => selectRow(r)}
                    className="menu-card"
                    style={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      padding: isNarrow ? "10px 10px" : "8px 10px",
                      border: "none",
                      borderBottom: "1px solid #f0f0f0",
                      borderRadius: 0,
                      background: sel ? "rgba(0,0,0,0.05)" : "transparent",
                      fontSize: listItemText,
                    }}
                    title="Select"
                  >
                    <div style={{ fontWeight: 600 }}>
                      {fmtNum(r.cut_w)} → {fmtNum(r.cut_h)} mm — Fee: {fmtNum(r.exw)}
                    </div>
                  </button>
                );
              })}
              {rows.length === 0 && !loadingList ? (
                <div style={{ padding: 8, ...smallText }}>No results</div>
              ) : null}
            </div>
          </section>

          {/* RIGHT: editable form */}
          <section className="box" style={{ marginTop: isNarrow ? 12 : 0 }}>
            <div style={fieldGrid}>
              <label>Width from (mm)</label>
              <input
                className="text-input"
                type="number"
                min="1"
                step="1"
                name="width_from"
                value={form.width_from}
                onChange={onChange}
                placeholder="e.g. 0"
              />

              <label>Width to (mm)</label>
              <input
                className="text-input"
                type="number"
                min="1"
                step="1"
                name="width_to"
                value={form.width_to}
                onChange={onChange}
                placeholder="e.g. 1200"
              />

              <label>Fee</label>
              <input
                className="text-input"
                type="number"
                step="0.01"
                min="0"
                name="fee"
                value={form.fee}
                onChange={onChange}
                placeholder="0"
              />
            </div>

            {errorSave ? (
              <div style={{ color: "crimson", marginTop: 10 }}>{errorSave}</div>
            ) : (
              <div style={{ marginTop: 10, ...smallText }}>
                {"Use New to add a row, or pick one on the left to edit."}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

// ---------- helpers ----------
function num(v) {
  if (v === "" || v === null || v === undefined) return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}
function fmtNum(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
